import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getPasses, createPass, deletePass, getVisitPurposes } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { 
  Shield, Sun, Moon, LogOut, User, Car, Plus, CalendarIcon, 
  Clock, Trash2, Archive, FileText, ClipboardList, Phone, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate, Link } from 'react-router-dom';

export default function RequesterDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [passes, setPasses] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passType, setPassType] = useState('person');
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedPass, setSelectedPass] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Form states
  const [personForm, setPersonForm] = useState({
    full_name: '',
    passport_series: '',
    passport_number: '',
    visit_purpose: '',
    visit_time: ''
  });

  const [vehicleForm, setVehicleForm] = useState({
    license_plate: '',
    car_brand: '',
    car_model: '',
    visit_purpose: '',
    contact_phone: '',
    driver_name: '',
    visit_time: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [passesRes, purposesRes] = await Promise.all([
        getPasses(false),
        getVisitPurposes()
      ]);
      setPasses(passesRes.data);
      setPurposes(purposesRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const resetForms = () => {
    setPersonForm({
      full_name: '',
      passport_series: '',
      passport_number: '',
      visit_purpose: '',
      visit_time: ''
    });
    setVehicleForm({
      license_plate: '',
      car_brand: '',
      car_model: '',
      visit_purpose: '',
      contact_phone: '',
      driver_name: '',
      visit_time: ''
    });
    setDate(null);
    setNotes('');
  };

  const handleCreatePass = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error('Выберите дату визита');
      return;
    }

    const visitTime = passType === 'person' ? personForm.visit_time : vehicleForm.visit_time;
    if (!visitTime) {
      toast.error('Выберите время визита');
      return;
    }

    setSubmitting(true);
    try {
      const passData = {
        pass_type: passType,
        visit_date: format(date, 'yyyy-MM-dd'),
        visit_time: visitTime,
        notes: notes || null,
        person_data: passType === 'person' ? {
          full_name: personForm.full_name,
          passport_series: personForm.passport_series,
          passport_number: personForm.passport_number,
          visit_purpose: personForm.visit_purpose
        } : null,
        vehicle_data: passType === 'vehicle' ? {
          license_plate: vehicleForm.license_plate,
          car_brand: vehicleForm.car_brand,
          car_model: vehicleForm.car_model,
          visit_purpose: vehicleForm.visit_purpose,
          contact_phone: vehicleForm.contact_phone,
          driver_name: vehicleForm.driver_name
        } : null
      };

      await createPass(passData);
      toast.success('Заявка создана');
      resetForms();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания заявки');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePass = async (id) => {
    if (!window.confirm('Удалить заявку?')) return;
    try {
      await deletePass(id);
      toast.success('Заявка удалена');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400">Ожидает</Badge>;
      case 'arrived':
        return <Badge variant="outline" className="border-emerald-500 text-emerald-600 dark:text-emerald-400">На территории</Badge>;
      case 'departed':
        return <Badge variant="outline" className="border-blue-500 text-blue-600 dark:text-blue-400">Выехал</Badge>;
      default:
        return null;
    }
  };

  const personPurposes = purposes.filter(p => p.type === 'person' && p.active);
  const vehiclePurposes = purposes.filter(p => p.type === 'vehicle' && p.active);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg font-black tracking-tighter">SENTINEL</h1>
                <p className="text-xs text-muted-foreground">Заявитель</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {user?.role === 'orp' && (
                <Link to="/permanent-passes">
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="permanent-passes-link">
                    <ClipboardList className="h-4 w-4" />
                    <span className="hidden sm:inline">Реестр</span>
                  </Button>
                </Link>
              )}
              <Link to="/archive">
                <Button variant="ghost" size="sm" className="gap-2" data-testid="archive-link">
                  <Archive className="h-4 w-4" />
                  <span className="hidden sm:inline">Архив</span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.full_name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-8 gap-8">
          {/* Create Pass Form */}
          <div className="lg:col-span-3">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Создать заявку
                </CardTitle>
                <CardDescription>Оформите пропуск для посетителя или автомобиля</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={passType} onValueChange={setPassType}>
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="person" className="gap-2" data-testid="tab-person">
                      <User className="h-4 w-4" />
                      Человек
                    </TabsTrigger>
                    <TabsTrigger value="vehicle" className="gap-2" data-testid="tab-vehicle">
                      <Car className="h-4 w-4" />
                      Автомобиль
                    </TabsTrigger>
                  </TabsList>

                  <form onSubmit={handleCreatePass} className="space-y-4">
                    {/* Date & Time - Common */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Дата визита</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                              data-testid="date-picker-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, 'dd.MM.yyyy', { locale: ru }) : 'Выберите'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-50" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={(d) => { setDate(d); }}
                              disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                              locale={ru}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Время визита <span className="text-muted-foreground text-xs">(самое раннее)</span></Label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="time"
                            value={passType === 'person' ? personForm.visit_time : vehicleForm.visit_time}
                            onChange={(e) => passType === 'person' 
                              ? setPersonForm({...personForm, visit_time: e.target.value})
                              : setVehicleForm({...vehicleForm, visit_time: e.target.value})
                            }
                            className="pl-10"
                            data-testid="visit-time"
                          />
                        </div>
                      </div>
                    </div>

                    <TabsContent value="person" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label>ФИО посетителя</Label>
                        <Input
                          placeholder="Иванов Иван Иванович"
                          value={personForm.full_name}
                          onChange={(e) => setPersonForm({...personForm, full_name: e.target.value})}
                          required={passType === 'person'}
                          data-testid="person-fullname"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Серия паспорта</Label>
                          <Input
                            placeholder="1234"
                            value={personForm.passport_series}
                            onChange={(e) => setPersonForm({...personForm, passport_series: e.target.value})}
                            maxLength={4}
                            data-testid="passport-series"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Номер паспорта</Label>
                          <Input
                            placeholder="567890"
                            value={personForm.passport_number}
                            onChange={(e) => setPersonForm({...personForm, passport_number: e.target.value})}
                            maxLength={6}
                            data-testid="passport-number"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Цель визита</Label>
                        <Select
                          value={personForm.visit_purpose}
                          onValueChange={(value) => setPersonForm({...personForm, visit_purpose: value})}
                          required={passType === 'person'}
                        >
                          <SelectTrigger data-testid="person-purpose">
                            <SelectValue placeholder="Выберите цель" />
                          </SelectTrigger>
                          <SelectContent>
                            {personPurposes.map(p => (
                              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TabsContent>

                    <TabsContent value="vehicle" className="space-y-4 mt-0">
                      <div className="space-y-2">
                        <Label>ФИО посетителя</Label>
                        <Input
                          placeholder="Иванов Иван Иванович"
                          value={vehicleForm.driver_name}
                          onChange={(e) => setVehicleForm({...vehicleForm, driver_name: e.target.value})}
                          data-testid="driver-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Номер автомобиля</Label>
                        <Input
                          placeholder="А123БВ777"
                          value={vehicleForm.license_plate}
                          onChange={(e) => setVehicleForm({...vehicleForm, license_plate: e.target.value.toUpperCase()})}
                          required={passType === 'vehicle'}
                          className="font-mono uppercase"
                          data-testid="license-plate"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Марка</Label>
                          <Input
                            placeholder="КамАЗ"
                            value={vehicleForm.car_brand}
                            onChange={(e) => setVehicleForm({...vehicleForm, car_brand: e.target.value})}
                            data-testid="car-brand"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Модель</Label>
                          <Input
                            placeholder="5490"
                            value={vehicleForm.car_model}
                            onChange={(e) => setVehicleForm({...vehicleForm, car_model: e.target.value})}
                            data-testid="car-model"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Цель визита</Label>
                        <Select
                          value={vehicleForm.visit_purpose}
                          onValueChange={(value) => setVehicleForm({...vehicleForm, visit_purpose: value})}
                        >
                          <SelectTrigger data-testid="vehicle-purpose">
                            <SelectValue placeholder="Выберите цель" />
                          </SelectTrigger>
                          <SelectContent>
                            {vehiclePurposes.map(p => (
                              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Контактный телефон</Label>
                        <Input
                          type="tel"
                          placeholder="+7 (999) 123-45-67"
                          value={vehicleForm.contact_phone}
                          onChange={(e) => setVehicleForm({...vehicleForm, contact_phone: e.target.value})}
                          data-testid="contact-phone"
                        />
                      </div>
                    </TabsContent>

                    {/* Notes field - common for both */}
                    <div className="space-y-2">
                      <Label>Примечание</Label>
                      <Textarea
                        placeholder="Дополнительная информация..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        data-testid="pass-notes"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={submitting}
                      data-testid="create-pass-btn"
                    >
                      {submitting ? 'Создание...' : 'Создать заявку'}
                    </Button>
                  </form>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Passes List */}
          <div className="lg:col-span-5">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Мои заявки</h2>
              <Badge variant="secondary">{passes.length} заявок</Badge>
            </div>

            {passes.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Нет активных заявок</p>
                  <p className="text-sm text-muted-foreground mt-1">Создайте первую заявку на пропуск</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {passes.map((pass, index) => (
                  <Card 
                    key={pass.id} 
                    className={`animate-fade-in cursor-pointer hover:shadow-md transition-all ${
                      pass.status === 'pending' ? 'status-pending' : 
                      pass.status === 'arrived' ? 'status-arrived' : 'status-departed'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`pass-card-${pass.id}`}
                    onClick={() => { setSelectedPass(pass); setDetailsOpen(true); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-md ${pass.pass_type === 'person' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                            {pass.pass_type === 'person' ? (
                              <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            ) : (
                              <Car className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="font-semibold">
                              {pass.pass_type === 'person' 
                                ? pass.person_data?.full_name 
                                : pass.vehicle_data?.license_plate}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {pass.pass_type === 'person' 
                                ? pass.person_data?.visit_purpose
                                : [pass.vehicle_data?.driver_name, pass.vehicle_data?.car_brand, pass.vehicle_data?.car_model, pass.vehicle_data?.visit_purpose].filter(Boolean).join(' • ')}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {format(new Date(pass.visit_date), 'dd.MM.yyyy')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {pass.visit_time}
                              </span>
                            </div>
                            {pass.pass_type === 'person' && (pass.person_data?.passport_series || pass.person_data?.passport_number) && (
                              <div className="text-xs text-muted-foreground font-mono">
                                Паспорт: {[pass.person_data?.passport_series, pass.person_data?.passport_number].filter(Boolean).join(' ')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusBadge(pass.status)}
                          {pass.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); handleDeletePass(pass.id); }}
                              data-testid={`delete-pass-${pass.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Pass Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Детали заявки
            </DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4">
              {/* Type indicator */}
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-md ${selectedPass.pass_type === 'person' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                  {selectedPass.pass_type === 'person' ? (
                    <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Car className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {selectedPass.pass_type === 'person' 
                      ? selectedPass.person_data?.full_name 
                      : selectedPass.vehicle_data?.license_plate}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedPass.pass_type === 'person' ? 'Посетитель' : 'Транспорт'}
                  </p>
                </div>
                <div className="ml-auto">
                  {getStatusBadge(selectedPass.status)}
                </div>
              </div>

              <Separator />

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Дата визита</p>
                  <p className="font-medium flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {format(new Date(selectedPass.visit_date), 'dd MMMM yyyy', { locale: ru })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Время визита</p>
                  <p className="font-medium flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {selectedPass.visit_time}
                  </p>
                </div>
              </div>

              {selectedPass.pass_type === 'person' ? (
                <div className="space-y-3">
                  {(selectedPass.person_data?.passport_series || selectedPass.person_data?.passport_number) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedPass.person_data?.passport_series && (
                        <div>
                          <p className="text-muted-foreground">Серия паспорта</p>
                          <p className="font-mono font-medium">{selectedPass.person_data.passport_series}</p>
                        </div>
                      )}
                      {selectedPass.person_data?.passport_number && (
                        <div>
                          <p className="text-muted-foreground">Номер паспорта</p>
                          <p className="font-mono font-medium">{selectedPass.person_data.passport_number}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-sm">Цель визита</p>
                    <p className="font-medium">{selectedPass.person_data?.visit_purpose}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedPass.vehicle_data?.driver_name && (
                    <div>
                      <p className="text-muted-foreground text-sm">ФИО посетителя</p>
                      <p className="font-medium">{selectedPass.vehicle_data.driver_name}</p>
                    </div>
                  )}
                  {(selectedPass.vehicle_data?.car_brand || selectedPass.vehicle_data?.car_model) && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedPass.vehicle_data?.car_brand && (
                        <div>
                          <p className="text-muted-foreground">Марка</p>
                          <p className="font-medium">{selectedPass.vehicle_data.car_brand}</p>
                        </div>
                      )}
                      {selectedPass.vehicle_data?.car_model && (
                        <div>
                          <p className="text-muted-foreground">Модель</p>
                          <p className="font-medium">{selectedPass.vehicle_data.car_model}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-sm">Цель визита</p>
                    <p className="font-medium">{selectedPass.vehicle_data?.visit_purpose}</p>
                  </div>
                  {selectedPass.vehicle_data?.contact_phone && (
                    <div>
                      <p className="text-muted-foreground text-sm">Контактный телефон</p>
                      <p className="font-medium flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {selectedPass.vehicle_data.contact_phone}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedPass.notes && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-muted-foreground text-sm mb-1">Примечание</p>
                  <p className="text-sm">{selectedPass.notes}</p>
                </div>
              )}

              {/* Created info */}
              <div className="text-xs text-muted-foreground pt-2 border-t">
                Создано: {format(new Date(selectedPass.created_at), 'dd.MM.yyyy HH:mm', { locale: ru })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
